// Focused military workspace: choose plan, layer and tool, then act on map.
// Real deployments and visibility filters stay available but collapsed.

import { useEffect, useMemo, useRef, useState } from 'react'
import { unitSymbolSvg, sidcSvg, UNIT_CATEGORIES } from '../utils/military'
import {
  isTacticalIcon, loadMilitaryCatalog, loadTacticalCollection, searchMilitaryCatalog, searchTacticalIcons,
  tacticalIconName, tacticalIconRef, tacticalIconSvg, TACTICAL_ICON_SOURCES,
} from '../utils/tacticalIcons'
import Icon from './Icons'
import '../styles/MilitaryPanel.css'

const TOOLS = [
  ['select', 'Sélection', '⌖'], ['symbol', 'Symbole', '◎'], ['arrow', 'Flèche', '→'], ['line', 'Ligne', '╱'],
  ['freehand', 'Crayon', '〰'], ['polygon', 'Zone', '⬡'], ['rectangle', 'Rectangle', '□'], ['circle', 'Cercle', '○'],
  ['label', 'Texte', 'T'], ['erase', 'Effacer', '⌫'],
]
const sidc = (set, entity) => `1003${set}0000${entity}0000`
const STANDARD_SYMBOLS = [
  ['Combat terrestre', 'Infanterie', '10', '121100'], ['Combat terrestre', 'Infanterie mécanisée', '10', '121102'],
  ['Combat terrestre', 'Blindés', '10', '120500'], ['Combat terrestre', 'Reconnaissance', '10', '121300'],
  ['Combat terrestre', 'Forces spéciales', '10', '121700'], ['Combat terrestre', 'Tireur d’élite', '10', '121500'],
  ['Appui', 'Artillerie', '10', '130300'], ['Appui', 'Mortier', '10', '130800'],
  ['Appui', 'Défense aérienne', '10', '130100'], ['Appui', 'Missile', '10', '130700'],
  ['Appui', 'Génie', '10', '140700'], ['Appui', 'Déminage', '10', '140800'],
  ['Commandement', 'Transmissions', '10', '111000'], ['Commandement', 'Renseignement', '10', '151000'],
  ['Commandement', 'Police militaire', '10', '141200'], ['Commandement', 'Affaires civiles', '10', '110200'],
  ['Soutien', 'Médical', '10', '161300'], ['Soutien', 'Maintenance', '10', '161100'],
  ['Soutien', 'Transport', '10', '163600'], ['Soutien', 'Ravitaillement', '10', '160200'],
  ['Aérien', 'Avion de chasse', '01', '110104'], ['Aérien', 'Bombardier', '01', '110103'],
  ['Aérien', 'Transport aérien', '01', '110107'], ['Aérien', 'Hélicoptère', '01', '110200'],
  ['Aérien', 'Drone aérien', '01', '110300'], ['Aérien', 'Reconnaissance aérienne', '01', '110111'],
  ['Naval', 'Porte-avions', '30', '120100'], ['Naval', 'Destroyer', '30', '120203'],
  ['Naval', 'Frégate', '30', '120204'], ['Naval', 'Patrouilleur', '30', '120500'],
  ['Équipement', 'Char', '15', '120200'], ['Équipement', 'Véhicule blindé', '15', '120100'],
  ['Équipement', 'Lance-missiles', '15', '111000'], ['Équipement', 'Radar', '15', '220300'],
  ['Infrastructure', 'Base militaire', '20', '120802'], ['Infrastructure', 'Aéroport / base aérienne', '20', '121301'],
  ['Infrastructure', 'Port / base navale', '20', '121309'], ['Infrastructure', 'Télécommunications', '20', '121202'],
  ['Événement', 'Patrouille', '40', '130100'], ['Événement', 'Manifestation', '40', '120100'],
  ['Événement', 'Explosion', '40', '110600'], ['Événement', 'Incendie', '40', '140100'],
].map(([group, name, set, entity], index) => ({ id: `standard-${index}`, group, name, sidc: sidc(set, entity) }))
const TYPE_LABELS = { marker: 'Symbole', arrow: 'Flèche', line: 'Ligne', polygon: 'Zone', label: 'Texte' }
const SYMBOL_PAGE_SIZE = 120
const validSidc = (value) => /^\d{20}$/.test(value) || /^\d{30}$/.test(value)
const toolHint = (tool) => ({
  select: 'Cliquez un symbole pour afficher ses poignées; glissez-le pour le déplacer.',
  symbol: 'Choisissez un symbole OTAN, puis cliquez sur la carte.',
  label: 'Réglez le texte, cliquez sur la carte, puis écrivez directement à cet endroit.',
  erase: 'Cliquez près d’un élément pour le supprimer.',
  arrow: 'Maintenez, glissez, relâchez. Aperçu et style suivent le pointeur.',
  line: 'Maintenez, glissez, relâchez. Aperçu et style suivent le pointeur.',
  freehand: 'Maintenez et dessinez librement. Relâchez pour terminer le tracé.',
  rectangle: 'Maintenez et glissez entre deux coins du rectangle.',
  circle: 'Maintenez du centre vers le bord du cercle.',
  polygon: 'Cliquez le contour. Cliquez Zone à nouveau pour fermer.',
}[tool] || 'Choisissez un outil pour commencer.')

const MilitaryPanel = ({
  categories, onToggleCategory, undeployedUnits, ownedCountryId,
  plans, activePlan, planTool, readOnlyPlan,
  onSelectPlan, onCreatePlan, onDeletePlan, onSharePlan, onClosePlan,
  onSetPlanTool, onUndoFeature, onSavePlan, planDirty,
  planningMode, planningOpen, deployHighlightId,
  planLayers, activeLayer, hiddenLayers,
  onSelectLayer, onAddLayer, onRenameLayer, onDeleteLayer, onToggleLayerVisibility,
  planColor, onSetPlanColor, planStyle, onSetPlanStyle,
  mySymbols, planSymbol, onSetPlanSymbol, planFeatures, onDeleteFeature,
  selectedFeature, onSelectFeature,
}) => {
  const [showAllUnits, setShowAllUnits] = useState(false)
  const [symbolQuery, setSymbolQuery] = useState('')
  const [symbolSource, setSymbolSource] = useState('milstd')
  const [catalogTick, setCatalogTick] = useState(0)
  const [catalogLoading, setCatalogLoading] = useState(false)
  const [visibleSymbolCount, setVisibleSymbolCount] = useState(SYMBOL_PAGE_SIZE)
  const [sidcDraft, setSidcDraft] = useState(isTacticalIcon(planSymbol) ? '' : (planSymbol || ''))
  const highlightRef = useRef(null)

  useEffect(() => {
    highlightRef.current?.scrollIntoView({ block: 'center', behavior: 'smooth' })
  }, [deployHighlightId, undeployedUnits.length])

  useEffect(() => {
    if (!isTacticalIcon(planSymbol)) setSidcDraft(planSymbol || '')
  }, [planSymbol])

  useEffect(() => {
    setVisibleSymbolCount(SYMBOL_PAGE_SIZE)
    if (symbolSource !== 'milstd-e' && !TACTICAL_ICON_SOURCES[symbolSource]) return
    let cancelled = false
    setCatalogLoading(true)
    const loading = symbolSource === 'milstd-e' ? loadMilitaryCatalog() : loadTacticalCollection(symbolSource)
    loading.then(() => {
      if (!cancelled) { setCatalogTick((tick) => tick + 1); setCatalogLoading(false) }
    }).catch(() => {
      if (!cancelled) setCatalogLoading(false)
    })
    return () => { cancelled = true }
  }, [symbolSource])

  useEffect(() => setVisibleSymbolCount(SYMBOL_PAGE_SIZE), [symbolQuery])

  const listedUnits = showAllUnits
    ? undeployedUnits
    : undeployedUnits.filter((u) => u.is_deployable || u.id === deployHighlightId)
  const symbols = useMemo(() => {
    const q = symbolQuery.trim().toLocaleLowerCase('fr')
    if (TACTICAL_ICON_SOURCES[symbolSource]) {
      return searchTacticalIcons(symbolSource, q).map((name) => ({
        id: tacticalIconRef(symbolSource, name), name: tacticalIconName(`${symbolSource}:${name}`),
        icon: tacticalIconRef(symbolSource, name),
      }))
    }
    if (symbolSource === 'milstd-e') return searchMilitaryCatalog(q)
    const all = symbolSource === 'saved'
      ? (mySymbols || [])
      : STANDARD_SYMBOLS.map((symbol) => ({ ...symbol, svg: sidcSvg(symbol.sidc, 28), common: true }))
    return q ? all.filter((s) => `${s.group || ''} ${s.name} ${s.sidc}`.toLocaleLowerCase('fr').includes(q)) : all
  }, [mySymbols, symbolQuery, symbolSource, catalogTick])
  const layerFeatures = (planFeatures || []).filter((f) => (f.layer || 'Calque 1') === activeLayer)
  const setStyleField = (field, value) => onSetPlanStyle({ ...planStyle, [field]: value })

  const renderPlanList = () => <div className="mil-plan-picker">
    <div className="mil-plan-intro">
      <span className="mil-plan-intro-icon"><Icon name="swords" size={20} /></span>
      <div><strong>Préparer une opération</strong><p>Plans privés, calques tactiques et lien de lecture seule.</p></div>
    </div>
    <button className="mil-primary wide" onClick={onCreatePlan} disabled={!ownedCountryId}>+ Nouveau plan</button>
    <div className="mil-plan-list">
      {plans.map((p) => <div className="mil-plan-card" key={p.id}>
        <button className="mil-plan-open" onClick={() => onSelectPlan(p)}>
          <strong>{p.name}</strong>
          <span>{p.feature_count ?? p.features?.length ?? 0} élément(s){p.share_token ? ' · partagé' : ''}</span>
        </button>
        <button className="mil-icon-btn danger" title="Supprimer" aria-label={`Supprimer ${p.name}`} onClick={() => onDeletePlan(p)}><Icon name="trash" size={14} /></button>
      </div>)}
      {!plans.length && <p className="mil-empty">Aucun plan. Créez-en un pour dessiner sans toucher aux déploiements réels.</p>}
    </div>
  </div>

  const renderLayerManager = () => <section className="mil-work-section">
    <div className="mil-section-head"><strong>2 · Calque</strong>{!readOnlyPlan && <button className="mil-link-btn" onClick={onAddLayer}>+ Ajouter</button>}</div>
    <div className="mil-layer-list">
      {(planLayers || []).map((name) => {
        const count = (planFeatures || []).filter((f) => (f.layer || 'Calque 1') === name).length
        const hidden = (hiddenLayers || []).includes(name)
        return <div className={`mil-layer ${activeLayer === name ? 'active' : ''}`} key={name}>
          <input type="checkbox" className="pr-check" checked={!hidden} onChange={() => onToggleLayerVisibility(name)} title="Afficher ce calque" />
          <button className="mil-layer-name" onClick={() => onSelectLayer(name)}><span>{name}</span><small>{count}</small></button>
          {!readOnlyPlan && <>
            <button className="mil-icon-btn" title="Renommer" onClick={() => onRenameLayer(name)}><Icon name="edit" size={12} /></button>
            {(planLayers || []).length > 1 && <button className="mil-icon-btn danger" title="Supprimer le calque" onClick={() => onDeleteLayer(name)}><Icon name="trash" size={12} /></button>}
          </>}
        </div>
      })}
    </div>
  </section>

  const renderStyle = () => {
    if (!['arrow', 'line', 'freehand', 'polygon', 'rectangle', 'circle', 'label'].includes(planTool)) return null
    return <div className="mil-style-row">
      <label className="mil-color" title="Couleur"><input type="color" value={planColor || '#e07070'} onChange={(e) => onSetPlanColor(e.target.value)} /></label>
      {planTool !== 'label' && <>
        <select value={planStyle.width || 'normal'} onChange={(e) => setStyleField('width', e.target.value)} aria-label="Épaisseur"><option value="thin">Trait fin</option><option value="normal">Trait normal</option><option value="thick">Trait épais</option></select>
        <select value={planStyle.dash || 'solid'} onChange={(e) => setStyleField('dash', e.target.value)} aria-label="Motif du trait"><option value="solid">Continu</option><option value="dash">Tirets</option><option value="dot">Points</option></select>
      </>}
      {planTool === 'arrow' && <select value={planStyle.heads ?? 1} onChange={(e) => setStyleField('heads', Number(e.target.value))} aria-label="Pointes"><option value="0">Sans pointe</option><option value="1">Une pointe</option><option value="2">Deux pointes</option></select>}
      {planTool === 'label' && <>
        <select value={planStyle.fontFamily || 'Rajdhani'} onChange={(e) => setStyleField('fontFamily', e.target.value)} aria-label="Police"><option>Rajdhani</option><option>Inter</option><option value="serif">Serif</option><option value="monospace">Monospace</option></select>
        <select value={planStyle.fontSize || 16} onChange={(e) => setStyleField('fontSize', Number(e.target.value))} aria-label="Taille"><option value="12">12 px</option><option value="16">16 px</option><option value="22">22 px</option><option value="30">30 px</option><option value="42">42 px</option></select>
        <label className="mil-bg-color" title="Fond du texte"><span>Fond</span><input type="color" value={planStyle.background || '#111111'} onChange={(e) => setStyleField('background', e.target.value)} /></label>
        <label className="mil-transparent"><input type="checkbox" className="pr-check" checked={planStyle.transparentBackground !== false} onChange={(e) => setStyleField('transparentBackground', e.target.checked)} /> transparent</label>
      </>}
    </div>
  }

  const renderSymbols = () => planTool === 'symbol' && <div className="mil-symbol-picker">
    <div className="mil-symbol-standard"><strong>Bibliothèque tactique complète</strong><small>1 674 entités MIL-STD-2525E / APP-6E, 4 134 Game Icons, 6 232 Tabler et 1 843 Lucide.</small></div>
    <div className="mil-symbol-sources" role="tablist" aria-label="Source des symboles">
      <button className={symbolSource === 'milstd' ? 'active' : ''} onClick={() => setSymbolSource('milstd')}>Favoris <small>{STANDARD_SYMBOLS.length}</small></button>
      <button className={symbolSource === 'milstd-e' ? 'active' : ''} onClick={() => setSymbolSource('milstd-e')}>MIL-STD <small>1.6k</small></button>
      <button className={symbolSource === 'game-icons' ? 'active' : ''} onClick={() => setSymbolSource('game-icons')}>Game <small>4k</small></button>
      <button className={symbolSource === 'tabler' ? 'active' : ''} onClick={() => setSymbolSource('tabler')}>Tabler <small>6k</small></button>
      <button className={symbolSource === 'lucide' ? 'active' : ''} onClick={() => setSymbolSource('lucide')}>Lucide <small>1.8k</small></button>
      {!!mySymbols?.length && <button className={symbolSource === 'saved' ? 'active' : ''} onClick={() => setSymbolSource('saved')}>Créés <small>{mySymbols.length}</small></button>}
    </div>
    <input className="mil-search" value={symbolQuery} onChange={(e) => setSymbolQuery(e.target.value)} placeholder="Chercher : tank, vehicle, weapon, building…" />
    <div className="mil-symbol-results"><span>{catalogLoading ? 'Chargement…' : `${symbols.length.toLocaleString('fr-FR')} symbole(s)`}</span>{TACTICAL_ICON_SOURCES[symbolSource] && <small>{TACTICAL_ICON_SOURCES[symbolSource].license}</small>}{symbolSource === 'milstd-e' && <small>MIT · milsymbol</small>}</div>
    <div className="mil-symbol-grid">
      {symbols.slice(0, visibleSymbolCount).map((s) => {
        const value = s.icon || s.sidc
        const svg = s.icon ? tacticalIconSvg(s.icon, 30) : (s.svg || sidcSvg(s.sidc, 28))
        return <button key={s.id} className={planSymbol === value ? 'active' : ''} onClick={() => onSetPlanSymbol(value)} title={`${s.group ? `${s.group} · ` : ''}${s.name}${s.sidc ? ` · ${s.sidc}` : ''}`}><span dangerouslySetInnerHTML={{ __html: svg }} /><small>{s.name}</small></button>
      })}
      {!catalogLoading && !symbols.length && <p className="mil-empty">Aucun symbole correspondant.</p>}
    </div>
    {symbols.length > visibleSymbolCount && <button className="mil-load-more" onClick={() => setVisibleSymbolCount((count) => count + SYMBOL_PAGE_SIZE)}>Afficher {Math.min(SYMBOL_PAGE_SIZE, symbols.length - visibleSymbolCount)} de plus</button>}
    {symbolSource === 'game-icons' && <p className="mil-symbol-credit">Game-icons.net · CC BY 3.0 · auteurs crédités par collection.</p>}
    <div className="mil-sidc-editor"><label>Code SIDC MIL-STD-2525 / APP-6<input className="mil-search" type="text" inputMode="numeric" maxLength="30" pattern="(?:[0-9]{20}|[0-9]{30})" placeholder="20 ou 30 chiffres" value={sidcDraft} onChange={(e) => { if (!/^\d{0,30}$/.test(e.target.value)) return; setSidcDraft(e.target.value); if (validSidc(e.target.value)) onSetPlanSymbol(e.target.value) }} /></label><p>2525D / APP-6D : 20 chiffres. 2525E / APP-6E : 30 chiffres.</p>{validSidc(sidcDraft) && <span className="mil-sidc-preview" dangerouslySetInnerHTML={{ __html: sidcSvg(sidcDraft, 44) }} />}</div>
  </div>

  const renderWorkspace = () => <div className="mil-workspace">
    <header className="mil-workspace-head">
      <div><span className="mil-eyebrow">1 · Plan actif</span><h3>{activePlan.name}{planDirty ? <i title="Modifications non sauvées">●</i> : null}</h3></div>
      <button className="mil-icon-btn" title="Fermer le plan" onClick={onClosePlan}><Icon name="close" size={15} /></button>
    </header>
    {readOnlyPlan && <div className="mil-readonly"><Icon name="lock" size={13} /> Lecture seule — plan partagé</div>}
    {renderLayerManager()}
    {!readOnlyPlan && <section className="mil-work-section">
      <div className="mil-section-head"><strong>3 · Outil</strong><span>{activeLayer}</span></div>
      <div className="mil-tool-grid">{TOOLS.map(([value, label, glyph]) => <button key={value} className={planTool === value ? 'active' : ''} onClick={() => onSetPlanTool(planTool === value ? null : value)}><b>{glyph}</b><span>{label}</span></button>)}</div>
      {renderStyle()}{renderSymbols()}
      <p className={`mil-tool-hint ${planTool ? 'armed' : ''}`}><Icon name="crosshair" size={13} /> {toolHint(planTool)}</p>
    </section>}
    <section className="mil-work-section">
      <div className="mil-section-head"><strong>Éléments · {activeLayer}</strong><span>{layerFeatures.length}</span></div>
      <div className="mil-feature-list">
        {layerFeatures.map((feature) => {
          const index = (planFeatures || []).indexOf(feature)
          return <div key={`${feature.feature_type}-${index}`} className={selectedFeature === index ? 'selected' : ''} onClick={() => onSelectFeature?.(index)}><span>{TYPE_LABELS[feature.feature_type] || feature.feature_type}</span><small>{feature.label || feature.sidc || ''}</small>{!readOnlyPlan && <button className="mil-icon-btn danger" title="Supprimer" onClick={(event) => { event.stopPropagation(); onDeleteFeature(index) }}><Icon name="trash" size={12} /></button>}</div>
        })}
        {!layerFeatures.length && <p className="mil-empty">Calque vide — choisissez un outil, puis agissez sur la carte.</p>}
      </div>
    </section>
    {!readOnlyPlan && <footer className="mil-work-actions">
      <button className="mil-secondary" onClick={onUndoFeature}><Icon name="undo" size={13} /> Annuler</button>
      <button className="mil-primary" onClick={onSavePlan} disabled={!planDirty}><Icon name="save" size={13} /> {planDirty ? 'Sauver' : 'Sauvé'}</button>
      <button className="mil-secondary" onClick={() => onSharePlan(activePlan)}><Icon name="link" size={13} /> Partager</button>
    </footer>}
  </div>

  return <div className="mil-panel">
    {(planningOpen || readOnlyPlan) && (activePlan ? renderWorkspace() : renderPlanList())}
    <details className="mil-aux" open={!planningOpen && !readOnlyPlan}>
      <summary><span><Icon name="layers" size={14} /> Affichage des unités</span><small>{UNIT_CATEGORIES.filter((c) => categories[c.key] !== false).length}/{UNIT_CATEGORIES.length}</small></summary>
      <div className="mil-category-grid">{UNIT_CATEGORIES.map((c) => <label key={c.key} className="mil-category-item"><input type="checkbox" className="pr-check" checked={categories[c.key] !== false} onChange={() => onToggleCategory(c.key)} /><span>{c.label}</span></label>)}</div>
    </details>
    {ownedCountryId && <details className="mil-aux" open={!!deployHighlightId}>
      <summary><span><Icon name="shield" size={14} /> {planningMode ? 'Unités à placer dans le plan' : 'Unités à déployer'}</span><small>{listedUnits.length}</small></summary>
      <label className="mil-show-all"><input type="checkbox" className="pr-check" checked={showAllUnits} onChange={() => setShowAllUnits((v) => !v)} /> Toutes les unités</label>
      <div className="mil-unit-list">{listedUnits.map((u) => {
        const highlighted = u.id === deployHighlightId
        return <div key={u.id} ref={highlighted ? highlightRef : null} className={`mil-unit-row ${highlighted ? 'highlight' : ''}`} draggable onDragStart={(e) => e.dataTransfer.setData('text/mil-deploy-unit', String(u.id))} title="Glisser sur la carte"><span dangerouslySetInnerHTML={{ __html: unitSymbolSvg(u, 22) }} /><span>{u.name}</span><small>{u.echelon?.name || ''}</small><b>⋮⋮</b></div>
      })}{!listedUnits.length && <p className="mil-empty">Aucune unité disponible.</p>}</div>
    </details>}
  </div>
}

export default MilitaryPanel
